import { FC, useContext, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

import { Dialog } from "components/Dialog";
import { useKpisCategoriesQuery } from "hooks/queries/kpis";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { ConfirmationDialog } from "components/ConfirmationDialog";
import { API_URL_KPIS } from "constants/api_urls";
import { AxiosContext } from "contexts/AxiosContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { CreateCategoryDialog } from "pages/Kpis/CategoryDialogs/CreateCategoryDialog";
import { Category } from "interfaces/kpis";
import { EditCategoryDialog } from "pages/Kpis/CategoryDialogs/EditCategoryDialog";

interface CategoryCreatePayload {
  name: string;
}

interface CategoryEditPayload {
  id: number;
  name: string;
}

export interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const ListCategoriesDialog: FC<Props> = ({
  isOpen,
  onClose
}): JSX.Element => {
  const { axios } = useContext(AxiosContext)!;
  const queryClient = useQueryClient();
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const {
    data: categories,
    isLoading: isCategoriesLoading,
    isFetching: isCategoriesFetching,
    isError: isCategoriesError
  } = useKpisCategoriesQuery({ page: 1, size: 999_999 });

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [categoryToDeleteId, setCategoryToDeleteId] = useState<number | null>(
    null
  );
  const [showCreateCategoryDialog, setShowCreateCategoryDialog] = useState(
    false
  );
  const [showEditCategoryDialog, setShowEditCategoryDialog] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<Category | null>(null);

  // #region Mutations
  const deleteCategoryMutation = useMutation(["delete-kpi-category"], {
    mutationFn: async (id: number): Promise<void> => {
      return axios.delete(`${API_URL_KPIS}/categories/${id}`);
    },
    onSuccess: async (): Promise<void> => {
      setShowDeleteDialog(false);

      addSnackbarToQueue({
        message: "Category was successfully deleted.",
        severity: "success"
      });

      await queryClient.invalidateQueries(["kpis", "progress", "kpis"]);
      await queryClient.invalidateQueries(["kpis", "progress", "sites_kpis"]);
      await queryClient.invalidateQueries(["kpis", "categories"]);
    },
    onError: (err: Error): void => {
      console.info(err);

      addSnackbarToQueue({
        message: "Failed to delete Category.",
        severity: "error"
      });
    }
  });

  const createCategoryMutation = useMutation(["create-kpi-category"], {
    mutationFn: async (newCategory: CategoryCreatePayload) => {
      const { name } = newCategory;

      return axios.post<CategoryCreatePayload, AxiosResponse<unknown>>(
        `${API_URL_KPIS}/categories/`,
        { name }
      );
    },
    onSuccess: async () => {
      setShowCreateCategoryDialog(false);

      addSnackbarToQueue({
        message: "New Category was successfully created.",
        severity: "success"
      });

      await queryClient.invalidateQueries(["kpis", "categories"]);
    },
    onError: () => {
      addSnackbarToQueue({
        message: "Failed to create new Category.",
        severity: "error"
      });
    }
  });

  const updateCategoryMutation = useMutation(["update-kpi-category"], {
    mutationFn: async (categoryToEdit: CategoryEditPayload) => {
      const { id, name } = categoryToEdit;

      return axios.patch<CategoryEditPayload, AxiosResponse<unknown>>(
        `${API_URL_KPIS}/categories/${id}`,
        { name }
      );
    },
    onSuccess: async () => {
      setShowEditCategoryDialog(false);

      addSnackbarToQueue({
        message: "Category was successfully updated.",
        severity: "success"
      });

      await queryClient.invalidateQueries(["kpis", "categories"]);
    },
    onError: () => {
      addSnackbarToQueue({
        message: "Failed to update Category.",
        severity: "error"
      });
    }
  });
  // #endregion

  const handleCreate = async (): Promise<void> => {
    await queryClient.invalidateQueries(["kpis", "categories"]);

    setShowCreateCategoryDialog(true);
  };

  const handleEdit = async (category: Category): Promise<void> => {
    await queryClient.invalidateQueries(["kpis", "categories"]);

    setShowEditCategoryDialog(true);
    setCategoryToEdit(category);
  };

  const handleDelete = (id: number): void => {
    if (!id) {
      return;
    }
    setCategoryToDeleteId(id);
    setShowDeleteDialog(true);
  };

  const handleClose = (): void => {
    onClose();
  };

  return (
    <>
      <Dialog
        dialogId="kpi-list-categories-dialog"
        title="Manage Categories"
        open={isOpen}
        size="medium"
        fullWidth={true}
        onClose={handleClose}
        data-qa="KPI-List-Categories-Dialog"
        confirmButton={<></>} // stupid workaround to remove the confirm button
        allowClickaway
      >
        <>
          <ProgressIndicatorContainer
            loading={isCategoriesLoading || isCategoriesFetching}
            error={isCategoriesError}
            errorMessage="An error occured while loading Categories."
          >
            <Button variant="contained" onClick={handleCreate}>
              Create Category
            </Button>

            <List>
              {categories?.results.map(category => (
                <ListItem key={category.id} divider disableGutters>
                  <ListItemText
                    primary={
                      <ListItemContent>
                        <Box>
                          <Typography>{category.name}</Typography>
                        </Box>
                        <Stack direction="row">
                          <Tooltip title="Edit category">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(category)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete category">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(category.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </ListItemContent>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </ProgressIndicatorContainer>
        </>
      </Dialog>

      {showCreateCategoryDialog && (
        <CreateCategoryDialog
          isOpen={showCreateCategoryDialog}
          onClose={() => setShowCreateCategoryDialog(false)}
          onConfirm={async values => {
            await createCategoryMutation.mutate(values);
            setShowCreateCategoryDialog(false);
          }}
        />
      )}

      {categoryToEdit && showEditCategoryDialog && (
        <EditCategoryDialog
          isOpen={showEditCategoryDialog}
          onClose={() => setShowEditCategoryDialog(false)}
          onConfirm={async values => {
            await updateCategoryMutation.mutate(values);
            setShowEditCategoryDialog(false);
          }}
          category={categoryToEdit}
        />
      )}

      {showDeleteDialog && categoryToDeleteId && (
        <ConfirmationDialog
          title="Are you sure you want to delete this Category?"
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={async () => {
            if (categoryToDeleteId) {
              await deleteCategoryMutation.mutate(categoryToDeleteId);
              setCategoryToDeleteId(null);
            }
          }}
        />
      )}
    </>
  );
};

const ListItemContent = styled("div")`
  align-items: center;
  display: grid;
  grid-template-columns: minmax(0, 1fr) min-content;
  gap: ${({ theme }) => theme.spacing(1)};
`;
ListItemContent.displayName = "ListItemContent";
